// src/components/PredProbaHistogram.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { CategoryScale, LinearScale, BarElement, Tooltip, Legend, Chart as ChartJS } from "chart.js";
import { usePredictions } from "../hooks/usePredictions";
import GoToDashboardButton from '../components/GoToDashboardButton';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function makeHistogram(data, bins = 20) {
  const counts = new Array(bins).fill(0);
  for (const v of data) {
    if (Number.isFinite(v)) {
      const idx = Math.min(bins - 1, Math.max(0, Math.floor(v * bins)));
      counts[idx] += 1;
    }
  }
  const labels = counts.map((_, i) => {
    const lo = (i / bins).toFixed(2);
    const hi = ((i + 1) / bins).toFixed(2);
    return `${lo}–${hi}`;
  });
  return { labels, counts };
}

export default function PredProbaHistogram({ runId }) {
  const { t } = useTranslation();
  const { rows, loading, error } = usePredictions(runId);
  const chartFrameRef = useRef(null);
  const [chartReady, setChartReady] = useState(false);

  // Call hooks unconditionally; derive safe defaults when data isn't ready
  const probs = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((r) => Number(r.pred_proba_pass))
      .filter((n) => Number.isFinite(n));
  }, [rows]);

  const { labels, counts } = useMemo(() => makeHistogram(probs, 20), [probs]);

  useEffect(() => {
    setChartReady(false);
    const frame = chartFrameRef.current;
    if (!frame) return undefined;

    let animationFrame = 0;
    const markReady = () => {
      animationFrame = window.requestAnimationFrame(() => {
        const rect = frame.getBoundingClientRect();
        setChartReady(rect.width > 0 && rect.height > 0);
      });
    };

    markReady();
    const observer = new ResizeObserver(markReady);
    observer.observe(frame);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [runId, labels.length, probs.length]);

  if (loading) return <div>{t("Loading probabilities…")}</div>;
  if (error) return <div style={{ color: "crimson" }}>{t("Failed to load predictions")}: {String(error.message || error)}</div>;
  if (!rows || rows.length === 0) return null;
  if (probs.length === 0) return <div>{t("No probability values found for this run.")}</div>;

  return (
    <div>
      <h4>{t("Probability of PASS (distribution)")}</h4>
      <GoToDashboardButton />
      <div ref={chartFrameRef} style={{ height: 280, position: "relative", width: "100%" }}>
        {chartReady && (
          <Bar
            key={`${runId}-${probs.length}`}
            data={{
              labels,
              datasets: [{ label: t("count"), data: counts }]
            }}
            options={{
              animation: false,
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } }
            }}
            redraw
          />
        )}
      </div>
    </div>
  );
}
